import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { RoleName } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { Spinner } from '@/components/ui';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { TrackerPage } from '@/pages/TrackerPage';
import { TimesheetPage } from '@/pages/TimesheetPage';
import { MyActivityPage } from '@/pages/MyActivityPage';
import { DownloadPage } from '@/pages/DownloadPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { AttendancePage } from '@/pages/AttendancePage';
import { TeamAttendancePage } from '@/pages/admin/TeamAttendancePage';
import { SalarySheetPage } from '@/pages/admin/SalarySheetPage';
import { SalarySettingsPage } from '@/pages/admin/SalarySettingsPage';
import { LeaveBankPage } from '@/pages/admin/LeaveBankPage';
import { LeavePage } from '@/pages/LeavePage';
import { HolidaysPage } from '@/pages/HolidaysPage';
import { HandbookPage } from '@/pages/HandbookPage';
import { LeaveRequestsPage } from '@/pages/admin/LeaveRequestsPage';
import { DashboardPage } from '@/pages/admin/DashboardPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { ProjectsPage } from '@/pages/admin/ProjectsPage';
import { ReportsPage } from '@/pages/admin/ReportsPage';
import { ScreenshotsPage } from '@/pages/admin/ScreenshotsPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { AuditPage } from '@/pages/admin/AuditPage';

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner className="h-8 w-8 text-brand-600" />
    </div>
  );
}

/** Gate for any authenticated route; also enforces the first-login password change. */
function RequireAuth({ children, roles }: { children: ReactNode; roles?: RoleName[] }) {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (roles && !hasRole(...roles)) {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <p className="text-lg font-semibold text-slate-900">Not available to your account</p>
        <p className="mt-1 text-sm text-slate-500">You do not have the role required to view this page.</p>
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  const { user, loading } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/change-password"
        element={loading ? <FullPageSpinner /> : user ? <ChangePasswordPage /> : <Navigate to="/login" replace />}
      />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<TrackerPage />} />
        <Route path="timesheet" element={<TimesheetPage />} />
        <Route path="my-activity" element={<MyActivityPage />} />
        <Route path="download" element={<DownloadPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="attendance/team" element={<RequireAuth roles={['ADMIN', 'MANAGER']}><TeamAttendancePage /></RequireAuth>} />
        <Route path="holidays" element={<HolidaysPage />} />
        <Route path="handbook" element={<HandbookPage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="leave/requests" element={<RequireAuth roles={['ADMIN', 'MANAGER']}><LeaveRequestsPage /></RequireAuth>} />
        <Route path="salary" element={<RequireAuth roles={['ADMIN']}><SalarySheetPage /></RequireAuth>} />
        <Route path="salary/settings" element={<RequireAuth roles={['ADMIN']}><SalarySettingsPage /></RequireAuth>} />
        <Route path="salary/leave-bank" element={<RequireAuth roles={['ADMIN']}><LeaveBankPage /></RequireAuth>} />

        <Route path="admin" element={<RequireAuth roles={['ADMIN', 'MANAGER']}><DashboardPage /></RequireAuth>} />
        <Route path="admin/reports" element={<RequireAuth roles={['ADMIN', 'MANAGER']}><ReportsPage /></RequireAuth>} />
        <Route path="admin/screenshots" element={<RequireAuth roles={['ADMIN', 'MANAGER']}><ScreenshotsPage /></RequireAuth>} />
        <Route path="admin/users" element={<RequireAuth roles={['ADMIN']}><UsersPage /></RequireAuth>} />
        <Route path="admin/projects" element={<RequireAuth roles={['ADMIN', 'MANAGER']}><ProjectsPage /></RequireAuth>} />
        <Route path="admin/settings" element={<RequireAuth roles={['ADMIN']}><SettingsPage /></RequireAuth>} />
        <Route path="admin/audit" element={<RequireAuth roles={['ADMIN']}><AuditPage /></RequireAuth>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
